import { useState } from 'react';
import { CircleNotchIcon } from '@phosphor-icons/react';
import { Badge } from 'src/components/ui/badge';
import { Switch } from 'src/components/ui/switch';
import { Label } from 'src/components/ui/label';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from 'src/components/ui/tabs';
import { useToast } from 'src/context/ToastContext';
import { useUpdateAgentDefinition } from 'src/api/rpc/hooks/agent-definitions/useAgentDefinitions';
import {
  AgentDefinitionFormProvider,
  useAgentDefinitionForm,
} from './AgentDefinitionFormContext';
import { AgentDefinitionAgentTab } from './AgentDefinitionAgentTab';
import { AgentDefinitionToolsTab } from './AgentDefinitionToolsTab';
import { AgentDefinitionPermissionsTab } from './AgentDefinitionPermissionsTab';
import { AgentDefinitionSessionsTab } from './AgentDefinitionSessionsTab';
import { AgentDefinitionSaveButton } from './AgentDefinitionSaveButton';
import { T, Var, useGT } from 'gt-react';

type BuilderTab = 'agent' | 'tools' | 'permissions' | 'sessions';

type AgentDefinitionBuilderProps = {
  agentDefinitionId: string;
  /** Which tabs to show. Defaults to all four. */
  tabs?: BuilderTab[];
  /** Hide the header (name, description, version badge). Useful inside a dialog that has its own header. */
  hideHeader?: boolean;
  /** Hide the active/inactive toggle. */
  hideActiveToggle?: boolean;
  /** Custom label for the save button. */
  saveLabel?: React.ReactNode;
  /** Called after a successful save. */
  onSaved?: () => void;
};

const DEFAULT_TABS: BuilderTab[] = [
  'agent',
  'tools',
  'permissions',
  'sessions',
];

const TAB_TRIGGER_MAP: Record<BuilderTab, React.ReactNode> = {
  agent: <T context="tab label, agent definition">Agent</T>,
  tools: <T context="tab label, agent tools">Tools</T>,
  permissions: <T context="tab label, agent permissions">Permissions</T>,
  sessions: <T context="tab label, agent sessions">Sessions</T>,
};

type BuilderInnerProps = {
  tabs: BuilderTab[];
  hideHeader: boolean;
  hideActiveToggle: boolean;
  saveLabel?: React.ReactNode;
};

function AgentDefinitionBuilderInner({
  tabs,
  hideHeader,
  hideActiveToggle,
  saveLabel,
}: BuilderInnerProps) {
  const gt = useGT();
  const { showToast } = useToast();
  const updateAgent = useUpdateAgentDefinition();

  const { definition, currentVersion, isLoading, error } =
    useAgentDefinitionForm();

  const [activeTab, setActiveTab] = useState<BuilderTab>(tabs[0] ?? 'agent');
  const resolvedActiveTab = tabs.includes(activeTab)
    ? activeTab
    : (tabs[0] ?? 'agent');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <CircleNotchIcon className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !definition) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">
          {error instanceof Error
            ? error.message
            : gt('Failed to load agent definition', {
                context: 'error loading background agent definition',
              })}
        </p>
      </div>
    );
  }

  function handleToggleActive() {
    if (!definition) return;
    const wasActive = definition.isActive;
    updateAgent.mutate(
      {
        agentDefinitionId: definition.id,
        isActive: !wasActive,
      },
      {
        onSuccess: () => {
          showToast({
            title: wasActive ? 'Agent deactivated' : 'Agent activated',
            message: `Agent is now ${wasActive ? 'inactive' : 'active'}.`,
            color: 'success',
          });
        },
        onError: (err) => {
          showToast({
            title: 'Update failed',
            message: err.message,
            color: 'error',
          });
        },
      },
    );
  }

  return (
    <>
      {!hideHeader && (
        <div className="mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold">{definition.name}</h1>
              {definition.description && (
                <p className="text-muted-foreground mt-1">
                  {definition.description}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">
                <T context="agent definition version badge">
                  v<Var>{currentVersion?.version ?? definition.version}</Var>
                </T>
              </Badge>
              {definition.templateType && (
                <Badge variant="outline">{definition.templateType}</Badge>
              )}
            </div>
          </div>
        </div>
      )}

      <Tabs
        value={resolvedActiveTab}
        onValueChange={(v) => setActiveTab(v as BuilderTab)}
        className="w-full"
      >
        <div className="relative">
          <TabsList>
            {tabs.map((tab) => (
              <TabsTrigger key={tab} value={tab}>
                {TAB_TRIGGER_MAP[tab]}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-4">
            {!hideActiveToggle && (
              <div className="flex items-center gap-2">
                <Switch
                  checked={definition.isActive}
                  onCheckedChange={handleToggleActive}
                  disabled={updateAgent.isPending}
                />
                <Label className="text-sm">
                  {definition.isActive
                    ? gt('Active', { context: 'agent definition is enabled' })
                    : gt('Inactive', {
                        context: 'agent definition is disabled',
                      })}
                </Label>
              </div>
            )}
            <AgentDefinitionSaveButton label={saveLabel} />
          </div>
        </div>

        <div className="mt-4">
          {tabs.includes('agent') && (
            <TabsContent
              value="agent"
              forceMount
              className="data-[hidden]:hidden"
            >
              <AgentDefinitionAgentTab />
            </TabsContent>
          )}
          {tabs.includes('tools') && (
            <TabsContent
              value="tools"
              forceMount
              className="data-[hidden]:hidden"
            >
              <AgentDefinitionToolsTab />
            </TabsContent>
          )}
          {tabs.includes('permissions') && (
            <TabsContent
              value="permissions"
              forceMount
              className="data-[hidden]:hidden"
            >
              <AgentDefinitionPermissionsTab />
            </TabsContent>
          )}
          {tabs.includes('sessions') && (
            <TabsContent
              value="sessions"
              forceMount
              className="data-[hidden]:hidden"
            >
              <AgentDefinitionSessionsTab />
            </TabsContent>
          )}
        </div>
      </Tabs>
    </>
  );
}

export function AgentDefinitionBuilder({
  agentDefinitionId,
  tabs,
  hideHeader = false,
  hideActiveToggle = false,
  saveLabel,
  onSaved,
}: AgentDefinitionBuilderProps) {
  const resolvedTabs = tabs ?? DEFAULT_TABS;
  return (
    <AgentDefinitionFormProvider
      key={agentDefinitionId}
      agentDefinitionId={agentDefinitionId}
      onSaved={onSaved}
    >
      <AgentDefinitionBuilderInner
        tabs={resolvedTabs}
        hideHeader={hideHeader}
        hideActiveToggle={hideActiveToggle}
        saveLabel={saveLabel}
      />
    </AgentDefinitionFormProvider>
  );
}
